// src/hooks/useKeyPointsExtraction.js

import { useState, useCallback } from 'react';
import { useEditor } from '../components/contexts/EditorContext';
import { useWorkspaces } from "../components/contexts/WorkspacesContext";
import { useNotes } from '../components/contexts/NotesContext';
import { getKeyPointsFromAI } from '../api/aiKeyPointsApi';
import { createNotesBatch } from '../api/notesApi';

const useKeyPointsExtraction = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(0);  
  
  const { editor } = useEditor();
  const { getSelectedDocument } = useWorkspaces();  // ✅ FIXED: was selectedNoteId
  const { fetchNotes } = useNotes();
  
  /**
   * Format the content for a consolidated sticky
   */
  const formatStickyContent = useCallback((title, items) => {
    let content = `${title.toUpperCase()}:\n\n`;
    items.forEach((item, index) => {
      content += `${index + 1}. ${item}\n\n`;
    });
    return content.trim();
  }, []);

  /**
   * Extract information without creating stickies - just return the AI results
   */
  const extractKeyPointsOnly = useCallback(async (options = {}, customEditor = null, customNoteId = null) => {
    const { 
      maxPoints = 5, 
      extractionTypes = ["keyPoints", "detailedInsights", "quotes", "completeThoughts"] 
    } = options;
    
    // Use passed parameters or fall back to context
    const editorToUse = customEditor || editor;
    const selectedDocument = getSelectedDocument();
    const noteIdToUse = customNoteId || selectedDocument?.public_id;  // ✅ FIXED
    
    if (!editorToUse || !noteIdToUse) {
      return { success: false, message: 'Editor or note not available' };
    }
    
    const content = editorToUse.root.innerHTML;
    if (!content.trim()) {
      return { success: false, message: 'Note is empty' };
    }
    
    setIsProcessing(true);
    setError(null);
    
    try {
      const result = await getKeyPointsFromAI(content, maxPoints, extractionTypes);
      
      if (result.token_usage) {
        setTokenUsage(result.token_usage);
      }
      
      return { 
        success: true, 
        data: result,
        extractionTypes: extractionTypes,
        tokenUsage: result.token_usage
      };
    } catch (err) {
        console.error('Error extracting information:', err);
        // Use the actual error message instead of generic text
        return { success: false, message: err.message || 'Failed to extract information' };
      } finally {
      setIsProcessing(false);
    }
  }, [editor, getSelectedDocument]);  // ✅ FIXED dependency

  /**
   * Create stickies from existing AI results without calling AI again
   */
  const createStickiesFromResults = useCallback(async (aiData, extractionTypes, customNoteId = null) => {
    // Use passed parameter or fall back to context
    const selectedDocument = getSelectedDocument();
    const noteIdToUse = customNoteId || selectedDocument?.public_id;  // ✅ FIXED
    
    if (!noteIdToUse) {
      return { success: false, message: 'No note selected' };
    }

    try {
      const typeDefinitions = [
        { key: "keyPoints", title: "Key Points" },
        { key: "detailedInsights", title: "Insights" },
        { key: "quotes", title: "Quotes" },
        { key: "biasDetection", title: "Bias Detection" },
        { key: "factsClaims", title: "Facts & Claims" },
        { key: "completeThoughts", title: "Complete Thoughts" }
      ];
      
      const typesToProcess = typeDefinitions.filter(
        def => extractionTypes.includes(def.key)
      );
      
      const stickiesToCreate = [];
      const startX = 40;
      const startY = 70;
      const offsetX = 30;
      const offsetY = 250;
      
      typesToProcess.forEach((type, index) => {
        const items = aiData[type.key];
        
        if (items && items.length > 0) {
          const formattedContent = formatStickyContent(type.title, items);
          
          stickiesToCreate.push({
            content: formattedContent,
            x: startX + (index * offsetX),
            y: startY + (index * offsetY),
            width: 300,
            height: 350,
            z_index: 1000 + index,
            type: 'regular'
          });
        }
      });
      
      if (stickiesToCreate.length === 0) {
        return { success: false, message: 'No content to create stickies from' };
      }
      
      console.log(`Creating batch of ${stickiesToCreate.length} stickies from existing data`);
      const createdNotes = await createNotesBatch(noteIdToUse, stickiesToCreate);
      console.log(`Successfully created ${createdNotes.length} notes`); 
      
      // ✅ FIXED: Use refreshNotes instead of fetchStickies
      if (fetchNotes) {  
        fetchNotes(true);
      }
      
      return { 
        success: true, 
        message: `Created ${createdNotes.length} notes!`, 
        count: createdNotes.length
      };
    } catch (err) {
      console.error('Error creating stickies from results:', err);
      return { success: false, message: 'Failed to create stickies' };
    }
  }, [getSelectedDocument, formatStickyContent, fetchNotes]);  // ✅ Use fetchNotes

  return {
    extractKeyPointsOnly,
    createStickiesFromResults,
    isProcessing,
    error,
    tokenUsage
  };
};

export default useKeyPointsExtraction;